/**
 * User CSS text → the declarations it is allowed to contribute.
 *
 * This is where spec R68 is *decided*, and it is a pure string function on purpose:
 * "nothing a user types can reach the GMS chrome, the nav, the pane, the panel or
 * another module, whatever they type" is then a property a test can hold to any
 * input, not a claim about a rendering path (ADR-4A).
 *
 * Q26 was decided as option (b) — the editor accepts the text as typed, and the
 * system applies **only the element's own declarations**. So:
 *
 * 1. Comments are removed. `/* … *\/` can hide anything, including an unbalanced
 *    brace, and the user gets no styling from a comment either way.
 * 2. The text is truncated at the first `{`, `}` or `@`. That single cut is what
 *    drops a second selector (`} .gms-nav { … }`), a nested block (`body { … }`) and
 *    every at-rule (`@import url(https://evil/x.css)`, `@media …`) before any of it
 *    can become a rule. The prototype concatenates instead and prefixes the result,
 *    which one stray `}` escapes — explicitly not carried.
 * 3. What is left is read as a `;`-separated declaration list, and a part survives
 *    only if it is `property: value` with a property that looks like a property and
 *    a value with no CSS punctuation left in it.
 * 4. `url(…)` values are dropped whatever they point at: a surviving one would make
 *    the GMS origin fetch a remote resource, which NFR A4 rules out — the card's
 *    styling must not be an outbound channel.
 * 5. At most `MAX_CUSTOM_CSS_DECLARATIONS` survive, so a pathological paste cannot
 *    push a keystroke past NFR A3's budget.
 *
 * Nothing is reported back to the user: dropping silently is what Q26 chose.
 */

import { MAX_CUSTOM_CSS_DECLARATIONS } from '../constants';

/** `/* … *\/`, including an unterminated one — everything after it goes. */
const COMMENT_PATTERN = /\/\*[\s\S]*?(?:\*\/|$)/g;

/** Where the element's own declaration list ends and CSS structure begins. */
const STRUCTURE_PATTERN = /[{}@]/;

/**
 * A property name: letters, digits and hyphens, starting with a letter or with the
 * `--` of a custom property.
 */
const PROPERTY_PATTERN = /^-{0,2}[a-zA-Z][-a-zA-Z0-9]*$/;

/** A value that still holds CSS punctuation is not a value we can vouch for. */
const UNSAFE_VALUE_PATTERN = /[{}@;<>\\]/;

/** Any `url(` — spaced, cased or not (NFR A4). */
const URL_FUNCTION_PATTERN = /url\s*\(/i;

const declarationOf = (part: string): string | null => {
  const separator = part.indexOf(':');
  if (separator === -1) {
    return null;
  }

  const property = part.slice(0, separator).trim();
  const value = part.slice(separator + 1).trim();

  if (property === '' || value === '' || !PROPERTY_PATTERN.test(property)) {
    return null;
  }
  if (UNSAFE_VALUE_PATTERN.test(value) || URL_FUNCTION_PATTERN.test(value)) {
    return null;
  }

  return `${property}: ${value};`;
};

/**
 * The declarations of one element, normalised to `property: value;` pairs and
 * joined by a single space. An empty string means "this element contributes no
 * rule" — which is what `composeCardStylesheet` uses to leave it out entirely.
 */
export const parseDeclarations = (text: string): string => {
  const withoutComments = text.replace(COMMENT_PATTERN, ' ');

  const structureAt = withoutComments.search(STRUCTURE_PATTERN);
  const declarationList =
    structureAt === -1 ? withoutComments : withoutComments.slice(0, structureAt);

  const declarations: string[] = [];
  declarationList.split(';').forEach((part) => {
    if (declarations.length >= MAX_CUSTOM_CSS_DECLARATIONS) {
      return;
    }
    const declaration = declarationOf(part);
    if (declaration !== null) {
      declarations.push(declaration);
    }
  });

  return declarations.join(' ');
};
